import * as React from "react";
import { Box, Stack, Typography, Select, MenuItem, TextField, Checkbox, FormControlLabel } from "@mui/material";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import ScaleRoundedIcon from "@mui/icons-material/ScaleRounded";
import { fetchMissionSpec, upsertMissionSpec } from "../../../api/queries";
import { createPendingEdits, subscribeToRows } from "../../../api/sheetSync";
import type { EditedBy, MissionSpecRow } from "../../../types/payload";
import { AuthorTip } from "../sheetAuthors";
import { formatOrbit, formatPlatform, type MissionOrbit, type MissionPlatform } from "../platformOrbit";

const MONO = "'Roboto Mono', 'Courier New', monospace";

// budget is a single column ('' / 'unlimited' / a numeric string). These
// three are the fixed presets; anything else typed in is "custom" -- that's
// a UI-only distinction (which controls to show), never stored separately.
const PRESET_BUDGETS: Record<string, string> = {
  "500000": "€500,000",
  "1000000": "€1,000,000",
  unlimited: "No limit",
};

type BudgetMode = "preset" | "custom";

type SpecFields = Omit<MissionSpecRow, "id" | "group_id" | "edited_by">;

// A group has a single spec row, so pending edits are tracked under one key.
const SPEC_KEY = "spec";

const DEFAULT_SPEC: SpecFields = {
  budget: "",
  main_aspect: "",
  weight_not_important: true,
  weight_kg: null,
};

const fieldLabelSx = {
  fontFamily: MONO,
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase" as const,
  color: "rgba(196,181,253,0.65)",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    color: "#FFFFFF",
    fontSize: 13.5,
    borderRadius: 1.5,
    bgcolor: "rgba(255,255,255,0.04)",
    "& fieldset": { borderColor: "rgba(168,85,247,0.3)" },
    "&:hover fieldset": { borderColor: "rgba(168,85,247,0.55)" },
    "&.Mui-focused fieldset": { borderColor: "#A78BFA" },
  },
};

function Field(props: { icon: React.ElementType; label: string; children: React.ReactNode }) {
  const Icon = props.icon;
  return (
    <Stack spacing={0.75}>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Icon sx={{ fontSize: 15, color: "#A78BFA" }} />
        <Typography sx={fieldLabelSx}>{props.label}</Typography>
      </Stack>
      {props.children}
    </Stack>
  );
}

export default function MissionSpecPanel(props: {
  groupId: string;
  platformType: MissionPlatform;
  orbitType: MissionOrbit;
  platformDetail: string | null;
  orbitDetail: string | null;
}) {
  const { groupId, platformType, orbitType, platformDetail, orbitDetail } = props;
  const [spec, setSpec] = React.useState<SpecFields>(DEFAULT_SPEC);
  const [budgetMode, setBudgetMode] = React.useState<BudgetMode>("preset");
  const [aspectText, setAspectText] = React.useState("");
  const [editedBy, setEditedBy] = React.useState<EditedBy>({});
  const aspectTimer = React.useRef<number | undefined>(undefined);
  const weightTimer = React.useRef<number | undefined>(undefined);
  const budgetTimer = React.useRef<number | undefined>(undefined);
  const pending = React.useRef(createPendingEdits());

  // Loads the group's spec and then keeps it in step with the other members'
  // edits. Fields this browser is still editing keep their local value.
  React.useEffect(() => {
    let mounted = true;

    const apply = (row: SpecFields & { edited_by?: EditedBy | null }, initial: boolean) => {
      setEditedBy(row.edited_by ?? {});
      const next = pending.current.merge(SPEC_KEY, {
        budget: row.budget,
        main_aspect: row.main_aspect,
        weight_not_important: row.weight_not_important,
        weight_kg: row.weight_kg === null ? null : Number(row.weight_kg),
      });
      setSpec(next);
      setAspectText(next.main_aspect);
      // An empty budget says nothing about the mode ("custom" with nothing
      // typed yet is a UI-only state), so it only resets on the first load.
      if (next.budget) setBudgetMode(next.budget in PRESET_BUDGETS ? "preset" : "custom");
      else if (initial) setBudgetMode("preset");
    };

    const load = (initial: boolean) =>
      fetchMissionSpec(groupId).then(({ data }) => {
        if (mounted) apply(data ?? DEFAULT_SPEC, initial);
      });

    const unsubscribe = subscribeToRows<MissionSpecRow>(
      "mission_specifications",
      `group_id=eq.${groupId}`,
      (change) => {
        if (mounted && change.type !== "DELETE") apply(change.row, false);
      },
      () => load(false)
    );
    load(true);

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [groupId]);

  const save = (patch: Partial<SpecFields>) => {
    pending.current.mark(SPEC_KEY, patch);
    setSpec((prev) => ({ ...prev, ...patch }));
    // Supabase's query builder is lazy -- the request only fires once
    // something consumes it via .then()/await, so this is chained rather
    // than called bare.
    upsertMissionSpec(groupId, patch).then(({ error }) => {
      if (error) console.error("Failed to save mission specification:", error.message);
    });
  };

  const changeAspect = (value: string) => {
    setAspectText(value);
    pending.current.mark(SPEC_KEY, { main_aspect: value });
    window.clearTimeout(aspectTimer.current);
    aspectTimer.current = window.setTimeout(() => save({ main_aspect: value }), 500);
  };

  const changeWeightKg = (value: string) => {
    const parsed = value === "" ? null : Number(value);
    if (value !== "" && (Number.isNaN(parsed) || parsed! < 0)) return;

    setSpec((prev) => ({ ...prev, weight_kg: parsed }));
    pending.current.mark(SPEC_KEY, { weight_kg: parsed });
    window.clearTimeout(weightTimer.current);
    weightTimer.current = window.setTimeout(() => save({ weight_kg: parsed }), 500);
  };

  const changeBudgetCustom = (value: string) => {
    if (value !== "" && (Number.isNaN(Number(value)) || Number(value) < 0)) return;

    setSpec((prev) => ({ ...prev, budget: value }));
    pending.current.mark(SPEC_KEY, { budget: value });
    window.clearTimeout(budgetTimer.current);
    budgetTimer.current = window.setTimeout(() => save({ budget: value }), 500);
  };

  return (
    <Box
      sx={{
        borderRadius: 3,
        p: { xs: 2.75, sm: 3.5 },
        border: "1px solid rgba(168,85,247,0.24)",
        background: "linear-gradient(160deg, rgba(255,255,255,0.055) 0%, rgba(255,255,255,0.015) 100%)",
        backdropFilter: "blur(14px)",
      }}
    >
      <Stack spacing={2.25}>
        <Typography sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: { xs: 16, sm: 17.5 } }}>
          Mission specifications
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, minmax(0, 1fr))" },
            gap: { xs: 2.25, sm: 2.5 },
          }}
        >
          <Field icon={Inventory2RoundedIcon} label="Platform">
            <Typography sx={{ color: "#FFFFFF", fontSize: 13.5 }}>
              {platformType === "Other" && platformDetail ? platformDetail : formatPlatform(platformType)}
            </Typography>
          </Field>

          <Field icon={PublicRoundedIcon} label="Orbit">
            <Typography sx={{ color: "#FFFFFF", fontSize: 13.5 }}>
              {orbitType === "Other" && orbitDetail ? orbitDetail : formatOrbit(orbitType)}
            </Typography>
          </Field>

          <Field icon={PaidRoundedIcon} label="Budget">
            <AuthorTip editedBy={editedBy} field="budget">
            <Stack spacing={0.75}>
              <Select
                size="small"
                displayEmpty
                value={budgetMode === "custom" ? "custom" : spec.budget}
                onChange={(e) => {
                  const selected = e.target.value;
                  if (selected === "custom") {
                    setBudgetMode("custom");
                    save({ budget: "" });
                    return;
                  }
                  setBudgetMode("preset");
                  save({ budget: selected });
                }}
                sx={{
                  fontSize: 13.5,
                  color: spec.budget || budgetMode === "custom" ? "#FFFFFF" : "rgba(226,232,240,0.45)",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(168,85,247,0.3)" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(168,85,247,0.55)" },
                  "& .MuiSvgIcon-root": { color: "rgba(226,232,240,0.6)" },
                }}
                MenuProps={{ PaperProps: { sx: { bgcolor: "#1A1023", color: "#FFFFFF" } } }}
              >
                <MenuItem value="" disabled sx={{ fontSize: 13, fontStyle: "italic" }}>
                  Select budget…
                </MenuItem>
                {Object.entries(PRESET_BUDGETS).map(([value, label]) => (
                  <MenuItem key={value} value={value} sx={{ fontSize: 13 }}>
                    {label}
                  </MenuItem>
                ))}
                <MenuItem value="custom" sx={{ fontSize: 13 }}>
                  Custom amount
                </MenuItem>
              </Select>

              {budgetMode === "custom" && (
                <TextField
                  size="small"
                  placeholder="Custom budget"
                  value={spec.budget}
                  onChange={(e) => changeBudgetCustom(e.target.value)}
                  InputProps={{
                    startAdornment: <Typography sx={{ fontSize: 12, color: "rgba(226,232,240,0.55)", mr: 0.5 }}>€</Typography>,
                  }}
                  sx={inputSx}
                />
              )}
            </Stack>
            </AuthorTip>
          </Field>

          <Field icon={TuneRoundedIcon} label="Main aspect">
            <AuthorTip editedBy={editedBy} field="main_aspect">
              <TextField
                size="small"
                placeholder="e.g. better data quality, higher resolution…"
                value={aspectText}
                onChange={(e) => changeAspect(e.target.value)}
                sx={inputSx}
              />
            </AuthorTip>
          </Field>

          <Field icon={ScaleRoundedIcon} label="Weight">
            <Stack spacing={0.75}>
              <AuthorTip editedBy={editedBy} field="weight_not_important">
              <FormControlLabel
                sx={{ mx: 0 }}
                control={
                  <Checkbox
                    checked={spec.weight_not_important}
                    onChange={(e) =>
                      save({ weight_not_important: e.target.checked, weight_kg: e.target.checked ? null : spec.weight_kg })
                    }
                    sx={{
                      p: 0.5,
                      mr: 1,
                      color: "rgba(196,181,253,0.6)",
                      "&.Mui-checked": { color: "#A78BFA" },
                    }}
                  />
                }
                label={<Typography sx={{ fontSize: 13, color: "rgba(226,232,240,0.8)" }}>Not important</Typography>}
              />
              </AuthorTip>

              {!spec.weight_not_important && (
                <AuthorTip editedBy={editedBy} field="weight_kg">
                  <TextField
                    size="small"
                    placeholder="Target weight"
                    value={spec.weight_kg ?? ""}
                    onChange={(e) => changeWeightKg(e.target.value)}
                    InputProps={{
                      endAdornment: <Typography sx={{ fontSize: 12, color: "rgba(226,232,240,0.55)" }}>kg</Typography>,
                    }}
                    sx={inputSx}
                  />
                </AuthorTip>
              )}
            </Stack>
          </Field>
        </Box>
      </Stack>
    </Box>
  );
}
